/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { UserAuthManager } from "@context/authentication";
import { User } from "oidc-client-ts";

/**
 * Derives the token lifetime in seconds from a refresh response, accepting
 * either an explicit `expires_in` or an absolute `expiration_time`.
 *
 * @param data - The parsed token refresh response
 * @returns The remaining lifetime in seconds
 * @throws {Error} If no positive expiration can be derived
 */
function computeExpiresIn(data: any): number {
  const expiresIn = data.expires_in
    ?? (data.expiration_time
      ? data.expiration_time - Math.floor(Date.now() / 1000)
      : null);
  if (!expiresIn || expiresIn <= 0) {
    throw new Error('Refresh response missing valid expiration');
  }
  return expiresIn;
}

/**
 * Performs the OAuth2 refresh-token grant against the authority's `/oauth2/token`
 * endpoint and persists the new tokens via the auth store.
 *
 * @param authority - The OIDC authority URL (the `/oauth2/oidc` suffix is stripped)
 * @returns A synthesized OIDC `User` carrying the refreshed access token
 * @throws {Error} If no refresh token is available or the token endpoint responds non-OK
 */
async function executeTokenRefresh(authority: string): Promise<User> {
  const { getRefreshToken, updateStoredTokens } = await import('@services/auth/auth');
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token available');

  // storeUser() below calls the real oidc-client-ts User.toStorageString() to
  // serialize -- a plain object shaped like a User (the previous approach)
  // has no such method and throws "e.toStorageString is not a function" the
  // moment a refresh actually succeeds. The existing stored user supplies the
  // profile/session_state/scope a refresh response doesn't carry.
  const existingUser = await UserAuthManager.getUser();
  if (!existingUser) throw new Error('No existing user to refresh');

  const baseUrl = authority.replace(/\/oauth2\/oidc\/?$/, '');
  const tokenUrl = `${baseUrl}/oauth2/token`;
  const { getRequiredEnvVar } = await import('@shared/utils/validate-env');
  const clientId = getRequiredEnvVar('REACT_APP_CLIENT_ID');

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: clientId,
  });

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) throw new Error(`Token refresh failed with status ${response.status}`);

  const data = await response.json();
  const expiresIn = computeExpiresIn(data);
  updateStoredTokens(data.access_token, expiresIn);

  return new User({
    id_token: data.id_token ?? existingUser.id_token,
    session_state: existingUser.session_state,
    access_token: data.access_token,
    refresh_token: data.refresh_token ?? refreshToken,
    token_type: data.token_type ?? existingUser.token_type,
    scope: existingUser.scope,
    profile: existingUser.profile,
    expires_at: Math.floor(Date.now() / 1000) + expiresIn,
    userState: existingUser.state,
    url_state: existingUser.url_state,
  });
}

/**
 * Refreshes the OIDC access token and stores the updated user, swallowing any
 * failure so callers can gracefully fall back to the existing token.
 *
 * @param authority - The OIDC authority URL used to build the token endpoint
 * @returns The refreshed user on success, or null when the refresh fails
 */
export async function refreshAuthToken(
  authority: string
): Promise<User | null | undefined> {
  try {
    const updatedUser = await executeTokenRefresh(authority);
    if (UserAuthManager && UserAuthManager.storeUser) {
      await UserAuthManager.storeUser(updatedUser);
    }
    return updatedUser;
  } catch (tokenRenewalError) {
    console.warn("[OIDCService] Token refresh failed, falling back to existing token", tokenRenewalError);
    return null;
  }
}
